import React from 'react';
import { Chip, Tooltip } from '@mui/material';
import { colourForDriverId } from '../theme/tokens';
import type { AnnualStrategicDriver } from '../types/models';

// ---------------------------------------------------------------------------
// DriverPill — the one way to render a strategic driver link next to a work
// item or project. Keeps colour assignment deterministic across the app so
// the same driver reads the same everywhere. When a driver is absent, we
// render an "Unaligned" pill in a muted neutral.
//
// Performance: `colourForDriverId` is a cheap string hash; no memoisation
// needed at the call site. Tooltip lazy-mounts, so there is no render cost
// until hover.
// ---------------------------------------------------------------------------

interface DriverPillProps {
  driver?: AnnualStrategicDriver | null;
  /** Size of the pill. Compact is used inside dense lists; standard in cards. */
  size?: 'compact' | 'standard';
  /** Optional click handler — only set if the parent wants the pill to link. */
  onClick?: () => void;
}

export function DriverPill({ driver, size = 'standard', onClick }: DriverPillProps) {
  if (!driver) {
    return (
      <Tooltip title="Not linked to a strategic driver" arrow>
        <Chip
          label="Unaligned"
          size={size === 'compact' ? 'small' : 'medium'}
          onClick={onClick}
          sx={{
            bgcolor: 'transparent',
            color: 'text.secondary',
            border: 1,
            borderColor: 'divider',
            borderStyle: 'dashed',
            fontSize: size === 'compact' ? 11 : 12,
            fontWeight: 500,
            height: size === 'compact' ? 20 : 24,
            cursor: onClick ? 'pointer' : 'default',
          }}
        />
      </Tooltip>
    );
  }

  const colour = colourForDriverId(driver.id);
  return (
    <Tooltip
      title={
        driver.progressPercentage !== undefined
          ? `${driver.title} — ${driver.progressPercentage}% of target`
          : driver.title
      }
      arrow
    >
      <Chip
        label={driver.title}
        size={size === 'compact' ? 'small' : 'medium'}
        onClick={onClick}
        sx={{
          // A 2px left swatch doubles as the colour marker; the rest of the
          // chip stays neutral so the palette does not turn noisy.
          position: 'relative',
          pl: size === 'compact' ? 1.25 : 1.5,
          bgcolor: 'transparent',
          color: 'text.primary',
          border: 1,
          borderColor: 'divider',
          fontSize: size === 'compact' ? 11 : 12,
          fontWeight: 500,
          height: size === 'compact' ? 20 : 24,
          maxWidth: 240,
          cursor: onClick ? 'pointer' : 'default',
          '& .MuiChip-label': {
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          },
          '&::before': {
            content: '""',
            position: 'absolute',
            left: 0,
            top: 4,
            bottom: 4,
            width: 3,
            borderRadius: 2,
            backgroundColor: colour,
          },
        }}
      />
    </Tooltip>
  );
}

export default DriverPill;
